import { ReferralPriority } from '@getvim-os/types';
import { getProviderBySpecialist } from './getPoviderBySpecialist';
import { formatReason, getFormatResponseToNotes, hasLeadingReachValue } from './getNotesFormatting';
import {
  LEADING_REACH_REFERRAL_ID_CODE,
  LEADING_REACH_STATUS_CODE,
} from '../../utils/leadingReach.consts';
import { UpdatableReferral } from '@getvim/internal-vim-os-sdk/types';
import { createLogger } from '../../utils';
import { ReferralUtilityResponseReferralResult } from '../../api/optum/types';
import { removeNullsValueProps } from '../../utils/filterNullValues';
import { withAsyncErrorHandling } from '../../utils/withErrorHandling';
import { sanitizeText } from '../../utils/sanitizeText';

const logger = createLogger('getUpdateReferralByQueryResponse');

/**
 * Feature-flag inputs, named rather than positional: they are all booleans, so a transposition at
 * a call site is invisible to the compiler.
 */
export type ReferralResponseFlags = {
  shouldUseLeadingReachProviderReferToName: boolean;
  shouldUseCuroNotesText?: boolean;
  shouldUseNewReferralIdFormat?: boolean;
};

const getUpdateReferralByQueryResponseFunc = async (
  data: ReferralUtilityResponseReferralResult,
  {
    shouldUseLeadingReachProviderReferToName,
    shouldUseCuroNotesText = false,
    shouldUseNewReferralIdFormat = false,
  }: ReferralResponseFlags,
): Promise<UpdatableReferral> => {
  const {
    specialist,
    specialtyMappingDescription: specialty,
    beginDate,
    endDate,
    referralReason,
    urgency,
    diagnosisCodes,
    numberOfVisits,
    provider,
    externalSystemIdentifiers,
  } = data;

  const targetProviderPayload = getProviderBySpecialist(specialist, specialty);

  // Presence-only signal for the Leading Reach fields now written unconditionally into the notes.
  // The removed detection flag used to log whether LR was recognised, which is how the Optum-side
  // payload change behind this ticket was spotted; without a replacement the next such change is
  // invisible until a customer reports it. Booleans + the Optum referral id only — no values.
  logger.info('Leading Reach fields in notes', {
    noPHI: true,
    optumReferralId: data.referralId,
    hasProviderPlanName: !!provider?.planName,
    hasTier: !!specialist?.tier,
    hasLeadingReachReferralId: hasLeadingReachValue(
      externalSystemIdentifiers,
      LEADING_REACH_REFERRAL_ID_CODE,
    ),
    hasLeadingReachStatus: hasLeadingReachValue(
      externalSystemIdentifiers,
      LEADING_REACH_STATUS_CODE,
    ),
  });

  logger.info('Before Leading Reach Provider modification', {
    noPHI: true,
    shouldUseLeadingReachProviderReferToName,
    shouldUseCuroNotesText,
    targetProviderPayload: targetProviderPayload?.demographics,
  });
  if (shouldUseLeadingReachProviderReferToName && provider) {
    const { identifiers } = provider;
    if (identifiers.length) {
      (targetProviderPayload as any).demographics = {
        firstName: '',
        lastName: identifiers[0].value,
      };
    }
  }

  logger.info('After Leading Reach Provider modification', {
    noPHI: true,
    targetProviderPayload: targetProviderPayload?.demographics,
  });

  // Generate reasons and notes
  const reasons = referralReason ? [formatReason(referralReason)] : undefined;
  const notes = await getFormatResponseToNotes(
    data,
    shouldUseCuroNotesText,
    shouldUseNewReferralIdFormat,
  );

  return removeNullsValueProps({
    targetProvider: targetProviderPayload!,
    basicInformation: {
      startDate: beginDate,
      endDate,
      reasons: reasons?.map((reason) => sanitizeText(reason)) ?? [],
      priority: ReferralPriority[urgency?.toUpperCase()],
      specialty,
      notes: sanitizeText(notes),
      numberOfVisits,
    },
    conditions: {
      diagnosis: diagnosisCodes?.map(({ description, value }) => {
        return { description, code: value };
      }),
    },
  });
};

export const getUpdateReferralByQueryResponse = withAsyncErrorHandling<
  typeof getUpdateReferralByQueryResponseFunc
>(getUpdateReferralByQueryResponseFunc);
